import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import TradeDrawer from "./TradeDrawer";
import TradeCards from "./TradeCards";
import { ERA_VIEWS } from "./eras";
import { isExpiryDay } from "./expiry";

/* ===== Dashboard — activity-sorted unified view of the 4 fib-vwap books =====
   One screen for NIFTY / BANKNIFTY / SENSEX / Guarded trades, newest ACTIVITY
   first (entry, tier booking, or any exit — whatever just happened is row 1).
   Rows with activity in the last minutes glow; the tab label carries an
   unseen-activity badge (cleared when this tab is opened). */

export const DASH_SEEN_KEY = "dashSeenTs";

/* latest activity on a trade: entry, tier/partial booking, or exit */
export function lastActivity(t) {
  const entry = Date.parse(t.time || t.placedAt) || 0;
  const book = Date.parse(t.trailExitTime || t.trailExitDate) || 0;
  const exit = Date.parse(t.exitTime) || 0;
  if (exit >= book && exit > entry) {
    const r = (t.exitReason || "").toUpperCase();
    const label = r.includes("TARGET") ? "TARGET HIT" : r.includes("STOP") ? "SL HIT"
      : r.includes("TRAIL") ? "TRAILING SL" : r.includes("BREAKEVEN") ? "BREAKEVEN"
      : r.includes("EOD") ? "EOD CLOSE" : r ? r.slice(0, 14) : "CLOSED";
    const icon = r.includes("TARGET") ? "🎯" : r.includes("STOP") ? "🛑" : "🏁";
    return { ts: exit, label, icon };
  }
  if (book > entry) {
    const label = t.tier2Done ? "TIER 2 BOOKED" : t.tier1Done ? "TIER 1 BOOKED" : "PARTIAL BOOKED";
    return { ts: book, label, icon: "🏦" };
  }
  return { ts: entry, label: "ENTERED", icon: "🆕" };
}

const instrOf = (t) => (t.strategy === "fib-vwap-guarded" ? `${t.index || "?"} Gd` : (t.index || "?"));
const dayStr = (d) => d.toLocaleDateString("en-CA");
const fmtT = (ts) => new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
const rup = (v) => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;

function livePnl(t, livePremiums) {
  if (t.status === "OPEN" && livePremiums[t.tradingsymbol] && t.entryPremium) {
    const q = t.remainingQty != null ? t.remainingQty : (t.qty || 0);
    return (t.trailExitPnl || 0) + Math.round((livePremiums[t.tradingsymbol] - t.entryPremium) * q);
  }
  return t.netPnl != null ? t.netPnl : t.grossPnl;
}

export default function Dashboard() {
  const [trades, setTrades] = useState([]);
  const [livePremiums, setLivePremiums] = useState({});
  const [drawerTrade, setDrawerTrade] = useState(null);
  const [fromDate, setFromDate] = useState(() => dayStr(new Date()));
  const [toDate, setToDate] = useState("");
  const [fltIndex, setFltIndex] = useState("ALL");
  const [fltStatus, setFltStatus] = useState("ALL");
  const [fltExpiry, setFltExpiry] = useState("ALL");
  const [now, setNow] = useState(Date.now());
  const [soundOn, setSoundOn] = useState(() => { try { return localStorage.getItem("dashSound") !== "0"; } catch { return true; } });

  // mark seen on mount + focus (clears the tab badge)
  useEffect(() => {
    const seen = () => { try { localStorage.setItem(DASH_SEEN_KEY, String(Date.now())); } catch {} };
    seen();
    window.addEventListener("focus", seen);
    return () => { seen(); window.removeEventListener("focus", seen); };
  }, []);

  // today-default rolls over at day change (same pattern as the index tabs)
  const autoDate = useRef(dayStr(new Date()));
  useEffect(() => {
    const refresh = () => {
      const today = dayStr(new Date());
      if (today !== autoDate.current) {
        setFromDate(prev => (prev === autoDate.current ? today : prev));
        autoDate.current = today;
      }
    };
    window.addEventListener("focus", refresh);
    const iv = setInterval(refresh, 60000);
    return () => { window.removeEventListener("focus", refresh); clearInterval(iv); };
  }, []);

  // book poll (10s) + recency clock
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get("/api/monitor/status", { timeout: 15000 });
        setTrades((res.data?.trades || []).filter(t => String(t.strategy || "").startsWith("fib-vwap")));
      } catch {}
      setNow(Date.now());
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  // live premiums for open trades (5s)
  const tradesRef = useRef(trades); tradesRef.current = trades;
  useEffect(() => {
    const tick = async () => {
      const open = tradesRef.current.filter(t => t.status === "OPEN" && t.tradingsymbol);
      for (const t of open.slice(0, 12)) {
        try {
          const res = await axios.get("/api/zerodha/option-ltp", { params: { token: t.optionToken, tradingsymbol: t.tradingsymbol }, timeout: 4000 });
          const ltp = res.data?.ltp ?? res.data?.data?.ltp;
          if (ltp) setLivePremiums(prev => ({ ...prev, [t.tradingsymbol]: ltp }));
        } catch {}
      }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, []);

  const rows = useMemo(() => {
    return trades
      .filter(t => {
        const d = new Date(t.time || t.placedAt);
        if (isNaN(d)) return false;
        const day = dayStr(d);
        if (fromDate && day < fromDate) return false;
        if (toDate && day > toDate) return false;
        if (fltIndex !== "ALL" && instrOf(t) !== fltIndex) return false;
        if (fltStatus !== "ALL" && t.status !== fltStatus) return false;
        if (fltExpiry === "EXPIRY" && !isExpiryDay(t)) return false;
        if (fltExpiry === "NONEXP" && isExpiryDay(t)) return false;
        return true;
      })
      .map(t => ({ t, act: lastActivity(t) }))
      .sort((a, b) => b.act.ts - a.act.ts);
  }, [trades, fromDate, toDate, fltIndex, fltStatus, fltExpiry]);

  const summary = useMemo(() => {
    const today = dayStr(new Date());
    const ts = trades.filter(t => String(t.time || t.placedAt).slice(0, 10) === today || dayStr(new Date(t.time || t.placedAt)) === today);
    const open = ts.filter(t => t.status === "OPEN");
    const net = ts.reduce((a, t) => a + (livePnl(t, livePremiums) || 0), 0);
    return { n: ts.length, open: open.length, net };
  }, [trades, livePremiums]);

  const glow = (ts) => {
    const age = (now - ts) / 60000;
    if (age < 2) return "color-mix(in srgb, var(--info) 22%, transparent)";
    if (age < 10) return "color-mix(in srgb, var(--info) 10%, transparent)";
    return undefined;
  };

  const indices = ["ALL", "NIFTY", "NIFTY Gd", "BANKNIFTY", "SENSEX"];

  return (
    <div>
      <div className="dashboard-header">
        <div>
          <h2>Dashboard</h2>
          <p className="dashboard-subtitle">All fib-vwap books, latest activity first — entry, tier booking, target or SL on top the moment it happens.</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 12.5 }}>
        <span>Today: <b>{summary.n}</b> trade{summary.n === 1 ? "" : "s"}</span>
        <span><b style={{ color: "#f59e0b" }}>{summary.open}</b> open</span>
        <span>Net <b className={summary.net >= 0 ? "positive" : "negative"}>{rup(summary.net)}</b></span>
        <button onClick={() => { const v = localStorage.getItem("dashSound") !== "0" ? "0" : "1"; try { localStorage.setItem("dashSound", v); } catch {} ; setSoundOn(v !== "0"); }}
          title="Sound on new trade placed (works from any tab while the app is open)"
          style={{ background: "var(--btn-muted)", color: soundOn ? "var(--pos-text)" : "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 12, fontSize: 10.5, fontWeight: 700, padding: "3px 10px", cursor: "pointer" }}>
          {soundOn ? "🔔 sound on" : "🔕 sound off"}
        </button>
        <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--muted2)" }}><span className="pulse-dot" style={{ display: "inline-block" }}></span> live · book 30s · premiums 10s</span>
      </div>

      <div className="ema-filters" style={{ marginBottom: 10, gap: 6, flexWrap: "wrap" }}>
        <input type="date" className="ema-select date-input" value={fromDate} onChange={e => setFromDate(e.target.value)} title="From date" style={{ minWidth: 120 }} />
        <span style={{ color: "var(--muted2)", fontSize: 11 }}>→</span>
        <input type="date" className="ema-select date-input" value={toDate} onChange={e => setToDate(e.target.value)} title="To date (empty = open-ended)" style={{ minWidth: 120 }} />
        {ERA_VIEWS.map(era => (
          <button key={era.name} onClick={() => { setFromDate(era.from); setToDate(""); }} title={era.title}
            style={{
              background: fromDate === era.from && !toDate ? "color-mix(in srgb, var(--info) 20%, transparent)" : "var(--btn-muted)",
              color: "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 12,
              fontSize: 10.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer",
            }}>{era.name}</button>
        ))}
        <select className="ema-select" value={fltIndex} onChange={e => setFltIndex(e.target.value)} style={{ minWidth: 90 }}>
          {indices.map(i => <option key={i} value={i}>{i === "ALL" ? "All books" : i}</option>)}
        </select>
        <select className="ema-select" value={fltStatus} onChange={e => setFltStatus(e.target.value)} style={{ minWidth: 80 }}>
          <option value="ALL">All status</option><option value="OPEN">Open</option><option value="CLOSED">Closed</option>
        </select>
        <select className="ema-select" value={fltExpiry} onChange={e => setFltExpiry(e.target.value)} style={{ minWidth: 100 }}>
          <option value="ALL">All expiries</option><option value="EXPIRY">Expiry day</option><option value="NONEXP">Non-expiry</option>
        </select>
      </div>

      <TradeCards trades={rows.map(r => r.t)} livePremiums={livePremiums} onSelect={setDrawerTrade} />

      <div className="stocks-table-wrapper tl-table-wrap">
        <table className="stocks-table">
          <thead><tr>
            <th>Activity</th><th>Book</th><th>Type</th><th>Fib</th><th>Option</th><th>Qty</th>
            <th>Entry</th><th>SL</th><th>Target</th><th>Buy Prem</th><th>Now / Sell</th><th>Net P&L</th><th>Status</th>
          </tr></thead>
          <tbody>
            {rows.map(({ t, act }) => {
              const pnl = livePnl(t, livePremiums);
              const open = t.status === "OPEN";
              const live = open && livePremiums[t.tradingsymbol];
              return (
                <tr key={t.id} onClick={() => setDrawerTrade(t)} title="Click for full trade details"
                  className={open ? "trade-open-row" : "trade-closed-row"}
                  style={{ cursor: "pointer", background: glow(act.ts) }}>
                  <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                    {act.icon} {fmtT(act.ts)}<div style={{ fontSize: 9.5, color: "var(--muted2)", fontWeight: 700, letterSpacing: 0.5 }}>{act.label}</div>
                  </td>
                  <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{instrOf(t)}</td>
                  <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                  <td><span className="fib-badge">{t.fibLevel || "-"}</span></td>
                  <td className="opt-cell">{t.signalType} {t.strike}
                    {isExpiryDay(t) && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: "var(--warn-text)", border: "1px solid var(--warn-text)", borderRadius: 3, padding: "0 3px" }}>EXP</span>}
                    {Array.isArray(t.aiReviews) && t.aiReviews.some(r => r.verdict === "EXIT") && <span style={{ marginLeft: 4 }}>🤖</span>}
                  </td>
                  <td>{t.qty}{t.trailExitDone && <div style={{ fontSize: 9.5, color: "var(--info-soft)" }}>booked {t.trailExitQty} · {t.remainingQty} run</div>}</td>
                  <td>{t.entry?.toFixed(1)}</td>
                  <td className="sl">{(t.currentSL ?? t.sl)?.toFixed(1)}</td>
                  <td>{t.target?.toFixed(1)}</td>
                  <td className="premium-cell">{t.entryPremium ? `₹${t.entryPremium.toFixed(1)}` : "-"}</td>
                  <td className="premium-cell">{live ? <span className="live-premium">₹{livePremiums[t.tradingsymbol].toFixed(1)}</span> : t.exitPremium ? `₹${t.exitPremium.toFixed(1)}` : "-"}</td>
                  <td className={pnl != null ? (pnl >= 0 ? "positive" : "negative") : ""} style={{ fontWeight: 700 }}>
                    {pnl != null ? rup(pnl) : "—"}{open && live && <span className="pulse-dot" style={{ display: "inline-block", marginLeft: 4 }}></span>}
                  </td>
                  <td><span className={`status-badge status-${(t.status || "").toLowerCase()}`}>{t.status}</span><span style={{ marginLeft: 4, fontSize: 8.5, fontWeight: 800, padding: "1px 5px", borderRadius: 3, letterSpacing: 0.5, background: t.simulated === false ? "#dc2626" : "var(--btn-muted)", color: t.simulated === false ? "#fff" : "var(--muted2)", border: t.simulated === false ? "none" : "1px solid var(--btn-border)" }}>{t.simulated === false ? "LIVE" : "PAPER"}</span>
                    {t.exitReason && <div style={{ fontSize: 9.5, color: "var(--muted2)" }}>{t.exitReason}</div>}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={13} style={{ color: "var(--muted2)" }}>No trades for the selected filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {drawerTrade && <TradeDrawer trade={drawerTrade} onClose={() => setDrawerTrade(null)} />}
    </div>
  );
}
